import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import * as UIcons from '@iconscout/react-unicons'
import Btn, { FormBtn } from 'components/Btn'
import Input from 'components/Input'
import useCountries from 'hooks/useCountries'
import { apiRequest } from 'store/Misc/apiRequest'
import { setFormLoading } from 'store/slices/miscSlice'
import { filterValues, destructureErrorMessage } from 'Helper'

// Fields not exposed as inputs here (state_id, street, building, google_map_url)
// are still carried through via `reset()` below and included in the submitted
// head_office payload unchanged, so editing here never clears data collected
// elsewhere in the app.
export default withTranslation()(function EditBusinessInfo({ t, info, handleClick }) {
  const dispatch = useDispatch()
  const { countries } = useCountries()
  const { data: categoriesRes } = useSWR('collections/categories?page=1&page-size=100')
  const categories = categoriesRes?.data ?? []
  const [step, setStep] = useState(1)
  const [logoPreview, setLogoPreview] = useState(info?.merchant_avatar ?? null)

  const method = useForm({ mode: 'all' })
  const { reset, handleSubmit, setValue } = method

  const handleLogoChange = (fileList) => {
    const file = fileList?.[0]
    if (!file) return
    setLogoPreview(URL.createObjectURL(file))
    setValue('avatar', file)
  }

  useEffect(() => {
    if (!info) return
    const category = info?.category ? JSON.parse(info.category) : null

    reset({
      name: info?.name,
      merchant_id: info?.merchant_id,
      registration_number: info?.registration_number,
      tax_id: info?.tax_id,
      category_id: category?.id,
      description: info?.about,
      business_email: info?.business_email,
      phone: info?.phone,
      alternate_phone: info?.alternate_phone,
      address: info?.head_office_address,
      country_id: info?.country_id,
      town: info?.head_office_city,
      postal_code: info?.postal_code,
      currency: info?.currency,
      timezone: info?.timezone,
      website: info?.website,
      state_id: info?.head_office_state_id,
      street: info?.head_office_street,
      building: info?.head_office_building,
      google_map_url: info?.google_map_url
    })
  }, [info, reset])

  const appendIfPresent = (formdata, key, value) => {
    if (value !== undefined && value !== null && value !== '') formdata.append(key, value)
  }

  const submit = async (payload) => {
    dispatch(setFormLoading())
    try {
      const formdata = new FormData()
      formdata.append('name', payload.name ?? '')
      formdata.append('category_id', payload.category_id ?? '')
      formdata.append('description', payload.description ?? '')
      formdata.append('website', payload.website ?? '')
      appendIfPresent(formdata, 'merchant_id', payload.merchant_id)
      appendIfPresent(formdata, 'registration_number', payload.registration_number)
      appendIfPresent(formdata, 'tax_id', payload.tax_id)
      if (payload.avatar) formdata.append('avatar', payload.avatar)

      await apiRequest({ method: 'post', url: 'settings/business-info', payload: formdata })

      const headOffice = {
        country_id: payload.country_id,
        state_id: payload.state_id,
        town: payload.town,
        street: payload.street,
        building: payload.building,
        address: payload.address,
        google_map_url: payload.google_map_url
      }
      if (payload.business_email) headOffice.business_email = payload.business_email
      if (payload.phone) headOffice.phone = payload.phone
      if (payload.alternate_phone) headOffice.alternate_phone = payload.alternate_phone
      if (payload.postal_code) headOffice.postal_code = payload.postal_code
      if (payload.currency) headOffice.currency = payload.currency
      if (payload.timezone) headOffice.timezone = payload.timezone

      await apiRequest({
        method: 'post',
        url: 'settings/business-info',
        payload: { head_office: headOffice }
      })

      toast.success(t('settings.business_organization.edit_success'))
      handleClick()
    } catch ({ message }) {
      destructureErrorMessage(message).map((msg) => toast.error(msg, { autoClose: 15000 }))
    } finally {
      dispatch(setFormLoading('close'))
    }
  }

  return (
    <div className="fixed inset-0 z-[5000] bg-black bg-opacity-30 flex items-center justify-center p-4" onClick={handleClick}>
      <div className="bg-white relative w-11/12 max-w-lg rounded-xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 md:p-8 pb-0 shrink-0">
          <div className="relative flex items-center justify-center mb-4">
            <Btn
              className="w-10 h-10 hover:bg-default absolute left-0"
              content={() => <UIcons.UilTimes className="text-secondary" />}
              click={handleClick}
            />

            <h3 className="text-xl font-bold text-dark-blue">{t('settings.business_organization.edit_title')}</h3>
          </div>

          <div className="w-full h-1.5 bg-border-lines rounded-full mb-6 flex gap-1 overflow-hidden">
            <div className="h-full bg-primary rounded-full flex-1" />
            <div className={`h-full rounded-full flex-1 ${step === 2 ? 'bg-primary' : 'bg-border-lines'}`} />
          </div>
        </div>

        <div className="overflow-y-auto px-6 md:px-8 pb-6 md:pb-8">
          <FormProvider {...method}>
            <form onSubmit={handleSubmit(submit)}>
            {
              step === 1 ? (
                <div key="step-1">
                  <div className="flex justify-center mb-6">
                    <div className="relative w-20 h-20">
                      {
                        logoPreview ? (
                          <img src={logoPreview} alt="" className="w-20 h-20 rounded-2xl object-cover" />
                        ) : (
                          <div className="w-20 h-20 rounded-2xl bg-icon-bg flex items-center justify-center">
                            <UIcons.UilBuilding className="text-primary" size={28} />
                          </div>
                        )
                      }

                      <button
                        type="button"
                        className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border border-border-lines shadow flex items-center justify-center"
                        onClick={() => document.getElementById('edit-business-logo')?.click()}
                      >
                        <UIcons.UilPen size={12} className="text-primary" />
                      </button>

                      <input
                        type="file"
                        id="edit-business-logo"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleLogoChange(e.target.files)}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <Input name="name" label={t('registration.field.bname')} variant="outlined" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 mb-3">
                    <Input name="merchant_id" label={t('settings.business_organization.fields.merchant_id')} variant="outlined" />
                    <Input name="registration_number" label={t('settings.business_organization.fields.registration_number')} variant="outlined" />
                  </div>

                  <div className="mb-3">
                    <Input name="tax_id" label={t('settings.business_organization.fields.tax_id')} variant="outlined" />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="category_id"
                      label={t('settings.business_info.category_id')}
                      variant="outlined"
                      options={filterValues({ options: categories, param: { label: 'name', value: 'id' } })}
                    />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="description"
                      label={t('settings.business_info.description.label')}
                      multiline
                      minRows={3}
                      variant="outlined"
                    />
                  </div>

                  <div className="flex justify-end mt-5">
                    <Btn
                      content={() => <>{t('misc.next')} <UIcons.UilArrowRight size={16} className="ml-2" /></>}
                      className="h-12 px-6 rounded-xl bg-primary text-white"
                      click={() => setStep(2)}
                    />
                  </div>
                </div>
              ) : (
                <div key="step-2">
                  <div className="mb-3">
                    <Input name="business_email" label={t('settings.business_organization.fields.business_email')} variant="outlined" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 mb-3">
                    <Input name="phone" label={t('settings.business_organization.fields.phone')} variant="outlined" />
                    <Input name="alternate_phone" label={t('settings.business_organization.fields.alternate_phone')} variant="outlined" />
                  </div>

                  <div className="mb-3">
                    <Input name="address" label={t('settings.head_office.address.label')} variant="outlined" />
                  </div>

                  <div className="mb-3">
                    <Input
                      name="country_id"
                      label={t('settings.head_office.country_id')}
                      variant="outlined"
                      options={countries ? filterValues({ options: countries, param: { value: 'id', label: 'name' } }) : []}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 mb-3">
                    <Input name="town" label={t('settings.business_organization.fields.city')} variant="outlined" />
                    <Input name="postal_code" label={t('settings.business_organization.fields.postal_code')} variant="outlined" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-5 mb-3">
                    <Input name="currency" label={t('settings.business_organization.fields.currency')} variant="outlined" />
                    <Input name="timezone" label={t('settings.business_organization.fields.timezone')} variant="outlined" />
                  </div>

                  <div className="flex justify-between mt-5">
                    <Btn
                      content={() => <><UIcons.UilArrowLeft size={16} className="mr-2" /> {t('misc.back')}</>}
                      className="h-12 px-6 rounded-xl border border-primary text-primary"
                      click={() => setStep(1)}
                      variant="outline"
                      color="#1976D2"
                    />

                    <FormBtn content={t('misc.save')} className="h-12 px-6 rounded-xl bg-primary text-white" />
                  </div>
                </div>
              )
            }
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  )
})
